import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Trophy, Plus, Loader2, Compass } from "lucide-react";
import { getTagFeed, getCategories } from "@/lib/api";
import { CategoryChips } from "@/components/CategoryChips";
import { NewsCard, HeroCard } from "@/components/NewsCard";
import { CardSkeleton, HeroSkeleton, EmptyState } from "@/components/Skeletons";
import { SEO } from "@/components/SEO";
import { useAuth } from "@/context/AuthContext";

const PAGE_SIZE = 16;

export default function TagView() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [label, setLabel] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const pagesRef = useRef(1);

  useEffect(() => { getCategories().then((r) => setCategories(r.categories || [])).catch(() => {}); }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    pagesRef.current = 1;
    getTagFeed(slug, { limit: PAGE_SIZE, skip: 0 })
      .then((r) => {
        if (!alive) return;
        setLabel(r.label || slug);
        const list = r.articles || [];
        setItems(list);
        setHasMore(list.length >= PAGE_SIZE);
      })
      .catch(() => { if (alive) setNotFound(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const skip = pagesRef.current * PAGE_SIZE;
      const r = await getTagFeed(slug, { limit: PAGE_SIZE, skip });
      const list = r.articles || [];
      pagesRef.current += 1;
      setItems((prev) => {
        const seen = new Set(prev.map((a) => a.article_id));
        return [...prev, ...list.filter((a) => !seen.has(a.article_id))];
      });
      setHasMore(list.length >= PAGE_SIZE);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const hasInterests = !!user && (((user.interests?.length || 0) + (user.interest_tags?.length || 0)) > 0 || !!user.zodiac_sign);
  const [hero, ...rest] = items;

  return (
    <div>
      <SEO title={label || "Tag"} description={`Tutte le notizie su ${label}.`} />
      <CategoryChips
        categories={categories}
        active="__tag"
        showForYou={hasInterests}
        showCreateFeed={!!user && !hasInterests}
      />
      <div className="max-w-[880px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-10 h-10 rounded-full bg-brandTertiary flex items-center justify-center">
            <Trophy size={18} className="text-brand" />
          </span>
          <div>
            <p className="text-[11px] font-mono font-bold uppercase tracking-wider text-brand">Calcio · Squadra</p>
            <h1 data-testid="tag-title" className="font-serif text-3xl sm:text-4xl font-semibold text-onSurface leading-none">{label}</h1>
          </div>
        </div>

        {loading ? (
          <div className="space-y-6">
            <HeroSkeleton />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">{Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}</div>
          </div>
        ) : notFound ? (
          <EmptyState icon={Compass} title="Tag non trovato" subtitle="Questa squadra non è tracciata."
            action={<Link to="/" className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors"><Compass size={15} /> Vai alla home</Link>} />
        ) : items.length === 0 ? (
          <EmptyState icon={Trophy} title={`Nessuna notizia su ${label} al momento`} subtitle="Torna più tardi: aggiorniamo di continuo."
            action={<Link to="/" data-testid="tag-discover-all" className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors"><Compass size={15} /> Scopri tutte le notizie</Link>} />
        ) : (
          <>
            <div className="space-y-5">
              {hero && <HeroCard article={hero} />}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {rest.map((a, i) => {
                    const wide = rest.length % 2 === 1 && i === rest.length - 1;
                    return (
                      <div key={a.article_id} className={wide ? "sm:col-span-2" : ""}>
                        <NewsCard article={a} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-8">
                <button onClick={loadMore} disabled={loadingMore} data-testid="tag-load-more"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-hair bg-surfaceSecondary text-onSurface hover:border-brand/50 hover:text-brand disabled:opacity-60 transition-colors text-sm font-semibold">
                  {loadingMore ? <><Loader2 size={16} className="animate-spin" /> Caricamento…</> : <><Plus size={16} /> Più articoli</>}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
